import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { CSSProperties } from 'react';
import { retireCandidatesApi } from '@/api/retire-candidates';
import { extractApiError } from '@/api/client';
import { ROBOT_STATUS_LABEL } from '@/lib/enum-labels';
import { kstYmd } from '@/lib/format-date';

// docs/work_log/v1031-remaining-work-batch-12 §3 — Retire 후보 admin page.
//
// 운영자가 windows UI 에서 "90+ 일 idle robot" 목록 확인 후 status 직접 처리.
// cron 의 자동 변경 안 함 정책 그대로 유지 — 본 페이지는 read-only 목록.
//
// 오너 확정 2026-08-20 요구 1 ⑤ — 제조팀(MFG_MANAGER, 레거시 MANAGER)도 이
// 화면을 본다(라우트·사이드바가 RETIRE_CANDIDATES_ROUTE_ROLES 공유, 서버 쌍은
// RETIRE_CANDIDATE_READ_ROLES — 비관리자는 자기 법인만). 화면 안에 폐기 실행
// 버튼은 없다(조회 전용) — RETIRED 전환은 로봇 상세의 상태 정정이 담당하고,
// 그 버튼은 결정 1 대로 관리자 전용(canCorrectToRetirement)이다. 여기에 실행
// 버튼을 추가하려면 그 경계를 먼저 확인하라.

export function RetireCandidatesPage() {
  const q = useQuery({
    queryKey: ['admin', 'retire-candidates'],
    queryFn: () => retireCandidatesApi.list(),
    staleTime: 5 * 60_000, // 5분 — cron 이 매일 05:00 KST 만 실행, 자주 새로고침 불필요
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          회수 검토 대기
        </h1>
      </header>

      {q.isLoading && (
        <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
          불러오는 중…
        </div>
      )}

      {q.error && (
        <div
          style={{
            padding: 12,
            background: 'var(--bg-danger-subtle)',
            border: '1px solid var(--fg-danger)',
            borderRadius: 'var(--r-card)',
            color: 'var(--fg-danger)',
            fontSize: 13,
          }}
        >
          {extractApiError(q.error)}
        </div>
      )}

      {q.data && (
        <>
          <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            전체 {q.data.scanned}대를 살펴봤고 후보는 {q.data.candidates.length}대입니다.
          </div>

          {/* batch-14 §6 — 분포 chip + age bucket chip (metric 있을 시) */}
          {q.data.metrics && q.data.candidates.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
                marginTop: 8,
                marginBottom: 8,
              }}
            >
              {Object.entries(q.data.metrics.byStatus)
                .sort((a, b) => b[1] - a[1])
                .map(([status, count]) => (
                  <span
                    key={status}
                    style={{
                      fontSize: 11,
                      padding: '3px 8px',
                      borderRadius: 'var(--r-pill)',
                      background: 'var(--bg-accent-subtle)',
                      color: 'var(--fg-default)',
                      fontFamily: 'var(--font-mono)',
                    }}
                    title={`${status} 상태인 후보 ${count}대`}
                  >
                    {status}: {count}
                  </span>
                ))}
              <span
                style={{
                  fontSize: 11,
                  padding: '3px 8px',
                  borderRadius: 'var(--r-pill)',
                  background: 'var(--bg-warning-subtle)',
                  color: 'var(--fg-on-warning-tint)',
                }}
                title="경과일 분포"
              >
                90-180d: {q.data.metrics.byAgeBucket['90-180']} · 180-365d:{' '}
                {q.data.metrics.byAgeBucket['180-365']} · 365+d:{' '}
                {q.data.metrics.byAgeBucket['365+']}
              </span>
              <span
                style={{
                  fontSize: 11,
                  padding: '3px 8px',
                  borderRadius: 'var(--r-pill)',
                  background: 'var(--bg-danger-subtle)',
                  color: 'var(--severity-urgent)',
                }}
                title="최고령 후보의 경과 일수"
              >
                최고령 {q.data.metrics.oldestDaysIdle}일
              </span>
            </div>
          )}

          {q.data.candidates.length === 0 ? (
            <div style={empty}>
              <div className="kuun-h4">검토 대기 대상이 없습니다</div>
              <div className="kuun-caption" style={{ marginTop: 4 }}>
                쉬고 있는 로봇은 모두 90일 안에 활동 기록이 있거나 이미 폐기 처리되었습니다.
              </div>
            </div>
          ) : (
            <div style={tableWrap}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={th}>Short SN</th>
                    <th style={th}>Long SN</th>
                    <th style={th}>현재 상태</th>
                    <th style={{ ...th, textAlign: 'right' }}>마지막 회수일</th>
                    <th style={{ ...th, textAlign: 'right' }}>경과</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.candidates
                    .slice()
                    .sort((a, b) => b.daysSinceLastReturn - a.daysSinceLastReturn)
                    .map((c) => (
                      <tr key={c.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                        <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                          {c.serialShort ?? '–'}
                        </td>
                        <td
                          style={{
                            ...td,
                            fontFamily: 'var(--font-mono)',
                            color: 'var(--fg-muted)',
                            fontSize: 11,
                          }}
                        >
                          {c.serialLong ?? '–'}
                        </td>
                        <td style={td}>
                          {ROBOT_STATUS_LABEL[c.status as keyof typeof ROBOT_STATUS_LABEL] ??
                            c.status}
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                          {/* 추가수정 2차(26-07-27) 정정2 — 마지막 회수일도 콘솔 표준
                              표기(kstYmd)로. ko-KR 로캘 표기("2026. 8. 26.")는 다른
                              목록의 "2026.08.26" 과 어긋나고 폭도 들쭉날쭉했다.
                              kstYmd 가 값 없음/파싱 불가를 null 로 주므로 기존 '–'
                              폴백을 그대로 유지한다. */}
                          {kstYmd(c.lastReturnedAt) ?? '–'}
                        </td>
                        <td
                          style={{
                            ...td,
                            textAlign: 'right',
                            fontFamily: 'var(--font-mono)',
                            color:
                              c.daysSinceLastReturn > 180
                                ? 'var(--fg-danger)'
                                : 'var(--dday-warning)',
                            fontWeight: 600,
                          }}
                        >
                          {c.daysSinceLastReturn}일
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <Link
                            to={`/robots/${c.id}`}
                            style={{ color: 'var(--fg-brand)', fontSize: 12 }}
                          >
                            상세 →
                          </Link>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const th: CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};
const td: CSSProperties = { padding: '8px 12px', verticalAlign: 'middle' };
const tableWrap: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  overflow: 'auto',
};
const empty: CSSProperties = {
  padding: 40,
  textAlign: 'center',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
};
